<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue'
import plural from 'plural-ru'
import { price } from '@/helpers/numberFormatters'

const props = defineProps<{
  purchaseData: {
    unit_name: string
    course_name: string
    unit_price: number
    course_price: number
    description: string
    units_to_purchase: number
    can_purchase_per_unit: boolean
    material_name: string
    material_id?: number
    unit_id?: number
    discount_reason?: string
  }
}>()
const emit = defineEmits(['purchase'])
const { purchaseData } = toRefs(props)

const purchaseMode = ref('course')

const purchaseOptions = computed(() => {
  return [
    {
      mode: 'unit',
      text: `Только для юнита \u00AB${purchaseData.value.unit_name}\u00BB`,
      price: purchaseData.value.unit_price
    },
    {
      mode: 'course',
      text: `Для всего курса \u00AB${purchaseData.value.course_name}\u00BB`,
      price: purchaseData.value.course_price
    }
  ]
})

const descParts = computed(() => purchaseData.value.description.split(/\n+/))

const unitCountStr = computed(() => {
  const num = purchaseData.value.units_to_purchase
  return [num, plural(num, 'юнит', 'юнита', 'юнитов')].join(' ')
})

watch(
  purchaseData,
  value => {
    purchaseMode.value = value.can_purchase_per_unit ? 'unit' : 'course'
  },
  { deep: true, immediate: true }
)

const formatPrice = (value: number) => price.rub(value)

function purchase() {
  emit('purchase', {
    mode: purchaseMode.value,
    data: purchaseData.value
  })
}
</script>

<template>
  <div class="material-purchase">
    <div class="material-purchase__header">
      <h3 class="text-h3">Вы можете купить</h3>
      <span v-if="purchaseData.can_purchase_per_unit" class="text-subheading" v-text="purchaseData.material_name" />
      <span v-else class="text-title">
        &laquo;{{ purchaseData.material_name }}&raquo; для всего курса &laquo;{{ purchaseData.course_name }}&raquo; за
        {{ formatPrice(purchaseData.course_price) }}
      </span>
    </div>

    <div v-if="purchaseData.can_purchase_per_unit" class="material-purchase__options">
      <div
        v-for="(item, i) in purchaseOptions"
        :key="i"
        class="material-purchase__option"
        :class="{ 'material-purchase__option_active': item.mode === purchaseMode }"
        @click="() => (purchaseMode = item.mode)"
      >
        <v-radio :value="item.mode" :active="item.mode === purchaseMode" name="payment-mode" />

        <div class="material-purchase__option-body">
          <div>
            <p v-text="item.text" />
            <p v-if="item.mode === 'course'" class="material-purchase__option-caption" v-text="unitCountStr" />
          </div>

          <div v-if="item.mode === 'course'">
            <div class="material-purchase__discount-badge">-10%</div>
          </div>
        </div>

        <span class="material-purchase__option-price" v-text="formatPrice(item.price)" />
      </div>
    </div>

    <div v-if="purchaseData.description || purchaseData.discount_reason" class="material-purchase__description">
      <p
        v-if="purchaseData.discount_reason"
        class="material-purchase__discount-reason"
        v-text="purchaseData.discount_reason"
      />

      <p v-for="(paragraph, i) in descParts" :key="i" v-text="paragraph" />
    </div>

    <div class="text-caption mt-1 red-text">
      Обратите внимание: материалы, которые вы покупаете, представлены в электронном виде.
    </div>

    <div class="material-purchase__actions">
      <v-button mode="primary block" @click="purchase"> Купить </v-button>
    </div>
  </div>
</template>

<style lang="scss">
.material-purchase {
  height: 100%;
  display: flex;
  flex-direction: column;
  // Gap between elements
  & > * + * {
    margin-top: 32px;
  }

  @include desktop {
    max-width: 500px;
  }

  &__header {
    display: flex;
    flex-direction: column;
    & > * + * {
      margin-top: 8px;
    }
  }

  &__options {
    display: flex;
    flex-direction: column;
    & > * + * {
      margin-top: 16px;
    }
  }

  &__option {
    display: flex;
    align-items: center;
    & > * + * {
      margin-left: 16px;
    }

    cursor: pointer;
    @include text-body-bold;
    color: $darkGray;
    border-radius: 8px;

    transition:
      border-color 0.3s ease,
      color 0.3s ease;

    &_active {
      border: 1px solid $blue;
      color: black;
    }

    @include desktop {
      padding: 16px;

      &:not(&_active) {
        border: 1px solid $lightGray;
      }
    }

    @include mobile {
      @include text-button;
      padding: 16px 0;

      &_active:not(:only-child) {
        border: none;
      }
    }
  }

  &__option:only-child {
    padding: 16px;
    border: 1px solid $blue;

    @include mobile {
      align-items: initial;
      flex-direction: column;

      & > * + * {
        margin-left: 0px;
        margin-top: 16px;
      }
    }
  }

  &__option-body {
    flex: 1;
    min-width: 0;

    display: flex;
    @include desktop {
      & > * + * {
        margin-left: 8px;
      }
    }

    @include mobile {
      flex-direction: column;
      & > * + * {
        margin-top: 8px;
      }
    }
  }

  &__option-caption {
    @include text-caption;
    font-weight: normal;
  }

  &__option-price {
    @include text-body-bold;
  }

  &__description {
    @include text-body;
    color: $black;

    display: flex;
    flex-direction: column;
    min-width: 0;
    & > * + * {
      margin-top: 8px;
    }
  }

  &__discount-reason {
    color: $red;
  }

  &__discount-badge {
    @include text-button;
    color: white;

    padding: 2px 6px;
    background: #ff4c5b;
    border-radius: 4px;

    display: inline-flex;
    align-items: center;
  }

  &__actions {
    display: flex;

    @include desktop {
      flex-direction: row;
      & > * + * {
        margin-left: 8px;
      }
    }

    @include mobile {
      flex: 1;
      justify-content: flex-end;
      flex-direction: column;
      & > * + * {
        margin-top: 8px;
      }
    }
  }
}
</style>
